import { createAsyncThunk } from "@reduxjs/toolkit";
import { SlotRepositoryImpl } from "../Data/Repositories/SlotRepositoryImpl";
import { CreateSlotDTO, Slot, SlotSearchParams, BatchSlotSideEffectList } from "../Model/Slot";
import { User } from "../../User/Model/User";
import { CreateSlotUseCase } from "../Domain/UseCase/CreateSlotUseCase";
import { UpdateSlotUseCase } from "../Domain/UseCase/UpdateSlotUseCase";
import { DeleteSlotUseCase } from "../Domain/UseCase/DeleteSlotUseCase";
import { ExtendSlotUseCase } from "../Domain/UseCase/ExtendSlotUseCase";
import { BatchExtendSlotUseCase } from "../Domain/UseCase/BatchExtendSlotUseCase";
import { BatchRefundSlotUseCase } from "../Domain/UseCase/BatchRefundSlotUseCase";
import { BatchCreateSlotUseCase } from "../Domain/UseCase/BatchCreateSlotUseCase";
import { BatchUpdateSlotUseCase } from "../Domain/UseCase/BatchUpdateSlotUseCase";
import { CheckSlotExistsUseCase } from "../Domain/UseCase/CheckSlotExistsUseCase";
import { GetSlotsUseCase } from "../Domain/UseCase/GetSlotsUseCase";
import { GetSlotsPaginationInfoUseCase } from "../Domain/UseCase/GetSlotsPaginationInfoUseCase";
import { GetSlotSummaryUseCase } from "../Domain/UseCase/GetSlotSummaryUseCase";
import { GetAllSlotsUseCase } from "../Domain/UseCase/GetAllSlotsUseCase";
import { RefundSlotUseCase } from "../Domain/UseCase/RefundSlotUseCase";
import { UploadSlotExcelUseCase } from "../Domain/UseCase/UploadSlotExcelUseCase";

const repo = new SlotRepositoryImpl();

/* ────────────────────────────────────────── */
/* CRUD */
export const createSlot = createAsyncThunk(
  "slot/create",
  async ({
    dto,
    me,
    tempId,
  }: {
    dto: CreateSlotDTO;
    me: User;
    tempId?: number;
  }) => new CreateSlotUseCase(repo).execute(dto, me),
);

export const batchCreateSlots = createAsyncThunk(
  "slot/batchCreate",
  async ({
    request,
    me,
    tempIds,
    optimisticAlreadyApplied,
  }: {
    request: { count: number; slots: CreateSlotDTO[] };
    me: User;
    tempIds?: number[];
    optimisticAlreadyApplied?: boolean;
  }) => new BatchCreateSlotUseCase(repo).execute(request, me),
);

export const updateSlot = createAsyncThunk(
  "slot/update",
  async ({ id, dto, me }: { id: number; dto: Partial<Slot>; me: User }) =>
    new UpdateSlotUseCase(repo).execute(id, dto, me)
);

export const deleteSlot = createAsyncThunk(
  "slot/delete",
  async ({
    id,
    me,
    optimisticAlreadyApplied,
  }: {
    id: number;
    me: User;
    optimisticAlreadyApplied?: boolean;
  }) => new DeleteSlotUseCase(repo).execute(id, me),
);

/* ────────────────────────────────────────── */
/*   상태 변경   */
export const extendSlot = createAsyncThunk(
  "slot/extend",
  async ({ id, days, me }: { id: number; days: number; me: User }) =>
    new ExtendSlotUseCase(repo).execute(id, days, me)
);

export const refundSlot = createAsyncThunk(
  "slot/refund",
  async ({ id, me }: { id: number; me: User }) =>
    new RefundSlotUseCase(repo).execute(id, me)
);

/* ────────────────────────────────────────── */
/*   일괄 처리   */
export const batchExtend = createAsyncThunk<
  BatchSlotSideEffectList,
  {
    ids: number[];
    days: number;
    me: User;
    optimisticAlreadyApplied?: boolean;
  }
>("slot/batchExtend", async ({ ids, days, me }) =>
  new BatchExtendSlotUseCase(repo).execute(ids, days, me),
);

export const batchRefund = createAsyncThunk<
  BatchSlotSideEffectList,
  {
    ids: number[];
    me: User;
    optimisticAlreadyApplied?: boolean;
  }
>("slot/batchRefund", async ({ ids, me }) =>
  new BatchRefundSlotUseCase(repo).execute(ids, me),
);

export const batchUpdateSlots = createAsyncThunk(
  "slot/batchUpdate",
  async ({ slots, me }: { slots: Partial<Slot>[]; me: User }) =>
    new BatchUpdateSlotUseCase(repo).execute(slots, me)
);

export const checkSlotExists = createAsyncThunk(
  "slot/checkExists",
  async ({ ids }: { ids: number[] }) =>
    new CheckSlotExistsUseCase(repo).execute(ids)
);

export const fetchSlotsBatchDetail = createAsyncThunk(
  "slot/fetchBatchDetail",
  async (slotIds: number[]) => repo.getBatchDetail(slotIds),
);

/* ────────────────────────────────────────── */
/*   조회   */
export const fetchSlots = createAsyncThunk(
  "slot/fetch",
  async ({ me, params }: { me: User; params?: SlotSearchParams }) => 
    new GetSlotsUseCase(repo).execute(me, params)
);

export const fetchSlotsPaginationInfo = createAsyncThunk(
  "slot/fetchPaginationInfo",
  async ({ me, params }: { me: User; params?: SlotSearchParams }) => 
    new GetSlotsPaginationInfoUseCase(repo).execute(me, params)
);

export const fetchSlotSummary = createAsyncThunk("slot/summary", async () =>
  new GetSlotSummaryUseCase(repo).execute()
);

/* ────────────────────────────────────────── */
/*   전체 조회   */
export const fetchAllSlots = createAsyncThunk(
  "slot/fetchAll",
  async ({ me, active }: { me: User; active?: boolean }) => 
    new GetAllSlotsUseCase(repo).execute(me, active)
);

/* ────────────────────────────────────────── */
/*   엑셀   */
export const uploadSlotExcel = createAsyncThunk(
  "slot/uploadExcel",
  async ({ file, me }: { file: File; me: User }) =>
    new UploadSlotExcelUseCase(repo).execute(file, me)
);
